if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const quantityInput = this.form.elements.namedItem('quantity');
        quantityInput?.closest('quantity-input')?.clampValueToRules({ fallbackToMinimum: true });

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/* Image variant quantities submitted together by the product Add to cart button */
(() => {
  const controlSelector = '[data-variant-cart-control]';

  const getMaxQuantity = (control) => {
    const quantity = Number.parseInt(control.dataset.variantMax || '', 10);
    return Number.isFinite(quantity) ? Math.max(0, quantity) : Number.POSITIVE_INFINITY;
  };

  const setQuantity = (control, quantity) => {
    const maxQuantity = getMaxQuantity(control);
    const safeQuantity = Math.max(0, Math.min(999, maxQuantity, quantity));
    const value = control.querySelector('[data-variant-cart-quantity]');
    const decrease = control.querySelector('[data-variant-cart-action="decrease"]');
    const increase = control.querySelector('[data-variant-cart-action="increase"]');
    const isAvailable = control.dataset.variantAvailable !== 'false' && maxQuantity > 0;

    if (value) {
      if ('value' in value) value.value = safeQuantity;
      else value.textContent = safeQuantity;
    }
    if (decrease) decrease.disabled = !isAvailable || safeQuantity === 0;
    if (increase) increase.disabled = !isAvailable || safeQuantity >= maxQuantity;
    return safeQuantity;
  };

  const initializeControls = (root = document) => {
    root.querySelectorAll(controlSelector).forEach((control) => setQuantity(control, 0));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-variant-cart-action]');
    if (!button || button.disabled) return;

    const control = button.closest(controlSelector);
    if (!control) return;

    const value = control.querySelector('[data-variant-cart-quantity]');
    const currentQuantity = Number.parseInt(value?.value || value?.textContent || '0', 10) || 0;
    const delta = button.dataset.variantCartAction === 'increase' ? 1 : -1;
    const appliedQuantity = setQuantity(control, currentQuantity + delta);
    const maxQuantity = getMaxQuantity(control);

    const status = control.querySelector('[data-variant-cart-status]');
    if (status) {
      status.textContent =
        delta > 0 && Number.isFinite(maxQuantity) && appliedQuantity >= maxQuantity
          ? 'Disponibilità massima: ' + maxQuantity
          : '';
    }
  });

  document.addEventListener('click', (event) => {
    const value = event.target.closest?.('[data-variant-cart-quantity]');
    if (!value) return;

    window.requestAnimationFrame(() => value.select());
  });

  document.addEventListener(
    'input',
    (event) => {
      const value = event.target.closest?.('[data-variant-cart-quantity]');
      if (!value) return;

      event.stopPropagation();
      const digits = value.value.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');
      if (value.value !== digits) value.value = digits;

      const control = value.closest(controlSelector);
      if (control) {
        const requestedQuantity = Number.parseInt(digits || '0', 10) || 0;
        const maxQuantity = getMaxQuantity(control);
        setQuantity(control, requestedQuantity);
        const status = control.querySelector('[data-variant-cart-status]');

        if (status) {
          status.textContent =
            Number.isFinite(maxQuantity) && requestedQuantity > maxQuantity
              ? 'Disponibilità massima: ' + maxQuantity
              : '';
        }
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      const value = event.target.closest?.('[data-variant-cart-quantity]');
      if (!value || event.key !== 'Enter') return;

      event.preventDefault();
      event.stopPropagation();
      value.blur();
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const value = event.target.closest?.('[data-variant-cart-quantity]');
      if (!value) return;

      event.stopPropagation();
      const control = value.closest(controlSelector);
      if (!control) return;

      setQuantity(control, Number.parseInt(value.value || '0', 10) || 0);
    },
    true
  );

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      const productForm = form.closest('product-form');
      if (!productForm) return;

      const scope = productForm.closest('product-info') || productForm.closest('.product') || document;
      const controls = Array.from(scope.querySelectorAll(controlSelector));
      if (!controls.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const items = controls
        .map((control) => ({
          id: Number(control.dataset.variantId),
          quantity:
            Number.parseInt(control.querySelector('[data-variant-cart-quantity]')?.value || '0', 10) || 0,
        }))
        .filter((item) => item.id && item.quantity > 0);

      productForm.handleErrorMessage?.();

      if (!items.length) {
        productForm.handleErrorMessage?.('Seleziona almeno una quantità.');
        return;
      }

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const submitButton = productForm.querySelector('[type="submit"]');
      const spinner = productForm.querySelector('.loading__spinner');

      submitButton?.setAttribute('aria-disabled', 'true');
      submitButton?.classList.add('loading');
      spinner?.classList.remove('hidden');

      const payload = { items };
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement?.(document.activeElement);
      }

      try {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Impossibile aggiungere gli articoli al carrello');
        }

        items.forEach((item) => {
          const control = controls.find((candidate) => Number(candidate.dataset.variantId) === item.id);
          if (!control) return;
          const currentMax = getMaxQuantity(control);
          if (Number.isFinite(currentMax)) {
            control.dataset.variantMax = String(Math.max(0, currentMax - item.quantity));
          }
        });
        controls.forEach((control) => {
          setQuantity(control, 0);
          const status = control.querySelector('[data-variant-cart-status]');
          if (status) status.textContent = '';
        });

        if (cart?.renderContents && result.sections) {
          const renderResult =
            Array.isArray(result.items) && result.items.length
              ? { ...result, ...result.items[0], sections: result.sections }
              : result;
          cart.renderContents(renderResult);
        }

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'variant-image-multi-add',
            productVariantId: items[0].id,
            cartData: result,
          });
        }
      } catch (error) {
        console.error(error);
        productForm.handleErrorMessage?.(error.message);
      } finally {
        submitButton?.classList.remove('loading');
        submitButton?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeControls(), { once: true });
  } else {
    initializeControls();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeControls(event.target);
  });
})();
